<!-- src/apps/secret/components/delivery/RecipientVerificationStep.vue -->

<script setup lang="ts">
  /**
   * Eightpoint: the code-entry step shown before a verify_recipient secret
   * can be opened. The recipient asks for a code (emailed to the address the
   * link was sent to), types it in, and once it checks out this browser may
   * reveal the secret. Emits `verified` so the page reloads the secret.
   */
  import { useI18n } from 'vue-i18n';
  import type { Secret } from '@/schemas/shapes/v3/secret';
  import { useApi } from '@/shared/composables/useApi';
  import { useSecretStore } from '@/shared/stores/secretStore';
  import { computed, nextTick, ref } from 'vue';

  interface Props {
    secretIdentifier: string;
    record: Secret;
  }

  const props = defineProps<Props>();
  const emit = defineEmits<{ (e: 'verified'): void }>();

  const { t } = useI18n();
  const $api = useApi();
  const secretStore = useSecretStore();

  const codeSent = ref(false);
  const code = ref('');
  const sending = ref(false);
  const verifying = ref(false);
  const error = ref('');
  const codeInput = ref<HTMLInputElement | null>(null);

  const hint = computed(() => props.record.recipient_hint || '');
  const endpoint = (suffix: string) => {
    const prefix = secretStore.apiMode === 'public' ? '/api/v3/guest' : '/api/v3';
    return `${prefix}/secret/${props.secretIdentifier}/recipient-code${suffix}`;
  };

  // FormError / LimitExceeded bodies carry the message in `error`.
  const messageFrom = (err: unknown, fallback: string) => {
    const data = (err as { response?: { data?: { error?: string; message?: string } } })?.response
      ?.data;
    return data?.error || data?.message || fallback;
  };

  const sendCode = async () => {
    if (sending.value) return;
    sending.value = true;
    error.value = '';
    try {
      const response = await $api.post(endpoint(''), {});
      if (response.data?.record?.verified) {
        emit('verified');
        return;
      }
      codeSent.value = true;
      code.value = '';
      await nextTick();
      codeInput.value?.focus();
    } catch (err) {
      error.value = messageFrom(err, t('web.secrets.delivery.send_code_failed'));
    } finally {
      sending.value = false;
    }
  };

  const verifyCode = async () => {
    if (verifying.value) return;
    verifying.value = true;
    error.value = '';
    try {
      await $api.post(endpoint('/verify'), { code: code.value });
      code.value = '';
      emit('verified');
    } catch (err) {
      error.value = messageFrom(err, t('web.secrets.delivery.verify_failed'));
    } finally {
      verifying.value = false;
    }
  };
</script>

<template>
  <div
    class="w-full rounded-lg bg-white p-8 dark:bg-gray-800"
    role="region"
    aria-labelledby="recipient-verification-heading"
    data-testid="recipient-verification-step">
    <h1
      id="recipient-verification-heading"
      class="text-xl font-bold text-gray-800 dark:text-gray-200">
      {{ t('web.secrets.delivery.verify_title') }}
    </h1>
    <p class="mt-2 text-base text-gray-600 dark:text-gray-400">
      <template v-if="hint">{{ t('web.secrets.delivery.verify_intro', { email: hint }) }}</template>
      <template v-else>{{ t('web.secrets.delivery.verify_intro_nohint') }}</template>
    </p>

    <p
      v-if="error"
      class="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-200"
      role="alert"
      data-testid="recipient-verification-error">
      {{ error }}
    </p>

    <div
      v-if="!codeSent"
      class="mt-6">
      <button
        type="button"
        class="w-full rounded-md bg-brand-500 px-4 py-3 text-lg font-semibold text-white
          hover:bg-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2
          disabled:cursor-not-allowed disabled:opacity-60"
        :disabled="sending"
        data-testid="recipient-send-code"
        @click="sendCode">
        {{ sending ? t('web.secrets.delivery.sending_code') : t('web.secrets.delivery.send_code') }}
      </button>
    </div>

    <form
      v-else
      class="mt-6 space-y-4"
      data-testid="recipient-code-form"
      @submit.prevent="verifyCode">
      <p
        class="text-sm text-gray-600 dark:text-gray-400"
        role="status"
        aria-live="polite">
        {{ t('web.secrets.delivery.code_sent', { email: hint || t('web.secrets.delivery.your_address') }) }}
      </p>
      <div>
        <label
          for="recipient-code-input"
          class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          {{ t('web.secrets.delivery.code_label') }}
        </label>
        <input
          id="recipient-code-input"
          ref="codeInput"
          v-model="code"
          type="text"
          inputmode="numeric"
          autocomplete="one-time-code"
          maxlength="7"
          pattern="[0-9 ]*"
          required
          class="w-full rounded-md border border-gray-300 px-3 py-2 text-center font-mono text-2xl tracking-[0.4em]
            focus:outline-none focus:ring-2 focus:ring-brand-500
            dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          :aria-invalid="error ? 'true' : undefined"
          data-testid="recipient-code-input" />
      </div>
      <button
        type="submit"
        class="w-full rounded-md bg-brand-500 px-4 py-3 text-lg font-semibold text-white
          hover:bg-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2
          disabled:cursor-not-allowed disabled:opacity-60"
        :disabled="verifying || code.replace(/\D/g, '').length !== 6"
        data-testid="recipient-verify-code">
        {{ verifying ? t('web.secrets.delivery.verifying') : t('web.secrets.delivery.verify_button') }}
      </button>
      <button
        type="button"
        class="w-full text-sm font-medium text-brand-600 hover:text-brand-500 focus:underline focus:outline-none
          disabled:opacity-60 dark:text-brand-400"
        :disabled="sending"
        data-testid="recipient-resend-code"
        @click="sendCode">
        {{ t('web.secrets.delivery.resend_code') }}
      </button>
    </form>
  </div>
</template>
